import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import EmojiPicker from "emoji-picker-react";
import {
  MessageCircle,
  Send,
  Loader2,
  Search,
  User,
  Phone,
  Copy,
  CheckCircle2,
  Info,
  RefreshCw,
  Sparkles,
  Bot,
  FileText,
  Music,
  Image,
  Smile,
  X,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatDay = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Hoje";
    const yest = new Date();
    yest.setDate(yest.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Ontem";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

const formatPhoneDisplay = (digits) => {
  if (!digits) return "";
  if (digits.startsWith("conv:")) return "";
  const s = digits.replace(/\D/g, "");
  if (!s) return "";
  // Brazil: +55 (DD) NNNNN-NNNN — exactly 12 or 13 digits starting with 55
  if (s.startsWith("55") && (s.length === 12 || s.length === 13)) {
    const dd = s.slice(2, 4);
    const rest = s.slice(4);
    const m = rest.length === 9 ? `${rest.slice(0, 5)}-${rest.slice(5)}` : `${rest.slice(0, 4)}-${rest.slice(4)}`;
    return `+55 (${dd}) ${m}`;
  }
  // International number — show as-is with + prefix (not garbled Brazilian format)
  if (s.length >= 8 && s.length <= 15) return `+${s}`;
  return "";
};

// Returns true if the name is meaningful (not null/empty/single char/dot)
const isValidName = (n) => n && n.trim().length > 1 && n.trim() !== ".";

// Get the best display name for a conversation
const getDisplayName = (c) => {
  const n = c.candidate_name || c.contact_name;
  if (isValidName(n)) return n.trim();
  const phone = formatPhoneDisplay(c.phone);
  if (phone) return phone;
  return "Contato";
};

// Get initials from a name (up to 2 chars)
const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

// Avatar component — shows photo if available, else coloured initials.
// Always renders a stable outer <div> so React never needs to swap the root
// element type during async blob-URL loads (swapping div↔img triggers the
// React "insertBefore: node is not a child" reconciliation error).
const ConvAvatar = ({ conv, size = "md" }) => {
  const base = size === "sm" ? "w-9 h-9" : "w-10 h-10";
  const name = getDisplayName(conv);
  const blobUrl = useMediaBlobUrl(conv.avatar_url || null);
  return (
    <div className={`${base} rounded-full shrink-0 overflow-hidden bg-slate-200 flex items-center justify-center`}>
      {blobUrl
        ? <img src={blobUrl} alt={name} className="w-full h-full object-cover" />
        : <span className="text-sm font-semibold text-slate-600">{getInitials(name)}</span>
      }
    </div>
  );
};

// Hook — fetches a protected media URL with Bearer auth, returns a blob: URL
const useMediaBlobUrl = (mediaUrl) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!mediaUrl) return;
    let objectUrl;
    const token = localStorage.getItem("rh_token");
    fetch(mediaUrl, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((err) => console.warn("Media load failed:", mediaUrl, err));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl]);
  return blobUrl;
};

// Renders a single message bubble — handles text, audio, image, document, emoji
const MessageBubble = ({ m }) => {
  const isOut = m.direction === "outbound";
  const bubbleClass = isOut
    ? "bg-emerald-600 text-white rounded-br-sm"
    : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm";

  const audioBlobUrl = useMediaBlobUrl(m.media_type === "audio" ? m.media_url : null);
  const imageBlobUrl = useMediaBlobUrl(m.media_type === "image" ? m.media_url : null);

  const renderContent = () => {
    if (m.media_type === "audio" && m.media_url) {
      return (
        <div className="flex flex-col gap-1">
          {audioBlobUrl ? (
            <audio controls src={audioBlobUrl} className="max-w-[240px] h-10 accent-emerald-500" />
          ) : (
            <div className="flex items-center gap-2 text-sm italic text-slate-400">
              <Music className="w-4 h-4 animate-pulse" /> Carregando áudio…
            </div>
          )}
          {m.body && <div className="text-sm whitespace-pre-wrap leading-relaxed mt-1">{m.body}</div>}
        </div>
      );
    }
    if (m.media_type === "image" && m.media_url) {
      return (
        <div className="flex flex-col gap-1">
          {imageBlobUrl ? (
            <img
              src={imageBlobUrl}
              alt={m.media_name || "imagem"}
              className="max-w-[240px] rounded-lg object-cover cursor-pointer"
              onClick={() => window.open(imageBlobUrl, "_blank")}
            />
          ) : (
            <div className="flex items-center gap-2 text-sm italic text-slate-400">
              <Image className="w-4 h-4 animate-pulse" /> Carregando imagem…
            </div>
          )}
          {m.body && <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>}
        </div>
      );
    }
    if ((m.media_type === "document" || m.media_type === "video") && m.media_url) {
      return (
        <div className="flex flex-col gap-1">
          <a
            href={m.media_url}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2 text-sm underline ${isOut ? "text-emerald-100" : "text-emerald-700"}`}
          >
            <FileText className="w-4 h-4 shrink-0" />
            {m.media_name || (m.media_type === "video" ? "Vídeo" : "Documento")}
          </a>
          {m.body && <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>}
        </div>
      );
    }
    if (!m.body && m.media_type) {
      // Media without URL yet
      return (
        <div className={`flex items-center gap-2 text-sm italic ${isOut ? "text-emerald-100" : "text-slate-500"}`}>
          {m.media_type === "audio" ? <Music className="w-4 h-4" /> : <Image className="w-4 h-4" />}
          {m.media_type === "audio" ? "Mensagem de áudio" : `Mídia: ${m.media_type}`}
        </div>
      );
    }
    // Plain text / emoji
    return <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>;
  };

  return (
    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm ${bubbleClass}`}>
      {renderContent()}
      <div className={`text-[10px] mt-1 text-right ${isOut ? "text-emerald-100" : "text-slate-400"}`}>
        {formatTime(m.created_at)}
      </div>
    </div>
  );
};

const WhatsappChatPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activePhone, setActivePhone] = useState("");
  const [activeMessages, setActiveMessages] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Tab title: show active contact when in a conversation, else conversation count
  const activeConversation = conversations.find((c) => c.phone === activePhone);
  const activeContactName = activeConversation ? getDisplayName(activeConversation) : "";
  useDocumentTitle(
    activeContactName
      ? `${activeContactName} · WhatsApp`
      : conversations.length
      ? `${conversations.length} conversas · WhatsApp`
      : "WhatsApp",
    { ready: !loadingConvos },
  );
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [copiedHook, setCopiedHook] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionMeta, setSuggestionMeta] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const lastUnreadByPhoneRef = useRef({});
  const notifPermissionAskedRef = useRef(false);

  const webhookUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/webhooks/synkly/inbound`;

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmojiPicker]);

  // Request browser notification permission once (after user lands on the page)
  useEffect(() => {
    if (notifPermissionAskedRef.current) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      // Defer to a microtask so it doesn't block UI
      setTimeout(() => {
        Notification.requestPermission().catch(() => {});
        notifPermissionAskedRef.current = true;
      }, 1500);
    } else {
      notifPermissionAskedRef.current = true;
    }
  }, []);

  const showInboundNotification = (convo) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    // Don't notify if the user is already looking at this conversation
    if (convo.phone === activePhone && document.visibilityState === "visible") return;
    try {
      const title = `Nova mensagem de ${getDisplayName(convo)}`;
      const body = (convo.last_message || "").slice(0, 120) || "Toque para abrir";
      const n = new Notification(title, {
        body,
        tag: `wa-${convo.phone}`,
        icon: "/favicon.ico",
        silent: false,
      });
      n.onclick = () => {
        window.focus();
        setActivePhone(convo.phone);
        n.close();
      };
    } catch (err) {
      // Some browsers throw if not in secure context. Not user-facing.
      console.debug("Browser blocked inbound notification:", err?.message);
    }
  };

  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/whatsapp/conversations`);
      const next = res.data || [];

      // Detect new inbound messages by comparing unread count vs previous snapshot
      const prev = lastUnreadByPhoneRef.current;
      const isFirstLoad = Object.keys(prev).length === 0;
      if (!isFirstLoad) {
        next.forEach((c) => {
          const before = prev[c.phone] || 0;
          if ((c.unread || 0) > before && c.last_direction === "inbound") {
            showInboundNotification(c);
          }
        });
      }
      lastUnreadByPhoneRef.current = Object.fromEntries(
        next.map((c) => [c.phone, c.unread || 0])
      );

      setConversations(next);
    } catch (err) {
      console.warn("Failed to load WhatsApp conversations:", err);
    } finally {
      setLoadingConvos(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone) => {
    if (!phone) return;
    setLoadingMessages(true);
    try {
      const res = await axios.get(
        `${API}/messages/whatsapp/${encodeURIComponent(phone)}`
      );
      setActiveMessages(res.data || []);
    } catch (err) {
      console.warn("Failed to load messages:", err?.response?.status ?? err?.message);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Deep link from notifications bell: /comunicacoes/whatsapp?phone=+55...
  useEffect(() => {
    const phoneParam = searchParams.get("phone");
    if (phoneParam && phoneParam !== activePhone) {
      setActivePhone(phoneParam);
    }
  }, [searchParams, activePhone]);

  // Load messages when active phone changes
  useEffect(() => {
    if (activePhone) loadMessages(activePhone);
  }, [activePhone, loadMessages]);

  // Polling for new messages every 6s
  useEffect(() => {
    const t = setInterval(() => {
      loadConversations();
      if (activePhone) loadMessages(activePhone);
    }, 6000);
    return () => clearInterval(t);
  }, [activePhone, loadConversations, loadMessages]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages]);

  const filteredConvos = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        (c.candidate_name || "").toLowerCase().includes(q) ||
        (c.contact_name || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q) ||
        (c.last_message || "").toLowerCase().includes(q)
    );
  }, [conversations, search]);

  // Sync auto-reply toggle whenever the active conversation has a candidate.
  useEffect(() => {
    setSuggestionMeta(null);
    if (!activeConversation?.candidate_id) {
      setAutoReplyEnabled(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/candidates/${activeConversation.candidate_id}`)
      .then((res) => {
        if (!cancelled) setAutoReplyEnabled(!!res.data?.auto_reply_enabled);
      })
      .catch(() => {
        if (!cancelled) setAutoReplyEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeConversation?.candidate_id]);

  const handleToggleAutoReply = async () => {
    if (!activeConversation?.candidate_id || togglingAuto) return;
    const next = !autoReplyEnabled;
    setTogglingAuto(true);
    try {
      const res = await axios.post(
        `${API}/candidates/${activeConversation.candidate_id}/auto-reply`,
        { enabled: next }
      );
      setAutoReplyEnabled(next);
      toast.success(res.data?.message || "Status atualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível alterar");
    } finally {
      setTogglingAuto(false);
    }
  };

  // ── Link-candidate modal handlers ─────────────────────────────────────────
  const handleLinkSearchChange = async (val) => {
    setLinkSearch(val);
    setLinkSearching(true);
    try {
      const res = await axios.get(`${API}/messages/whatsapp/candidates/search`, {
        params: { q: val },
      });
      setLinkResults(res.data || []);
    } catch {
      setLinkResults([]);
    } finally {
      setLinkSearching(false);
    }
  };

  const handleOpenLinkModal = () => {
    setLinkSearch("");
    setLinkResults([]);
    setLinkModalOpen(true);
    // Pre-load all candidates
    handleLinkSearchChange("");
  };

  const handleLinkCandidate = async (candidate) => {
    if (linking || !activePhone) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/messages/whatsapp/link-candidate`, {
        phone: activePhone,
        candidate_id: candidate.id,
      });
      toast.success(res.data?.message || "Conversa vinculada com sucesso!");
      setLinkModalOpen(false);
      // Refresh conversations so candidate_id appears and Maya button activates
      await loadConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao vincular candidato");
    } finally {
      setLinking(false);
    }
  };

  const handleSuggest = async () => {
    if (!activeConversation?.candidate_id || suggesting) return;
    setSuggesting(true);
    setSuggestionMeta(null);
    try {
      const res = await axios.post(`${API}/messages/whatsapp/suggest`, {
        candidate_id: activeConversation.candidate_id,
        incoming_text: "",
      });
      const text = (res.data?.suggestion || "").trim();
      if (!text) {
        toast.error("A IA não conseguiu gerar uma sugestão agora");
        return;
      }
      setDraft(text);
      setSuggestionMeta({
        intent: res.data?.intent,
        confidence: res.data?.confidence,
        reasoning: res.data?.reasoning,
      });
      toast.success(
        `IA sugeriu uma resposta (${res.data?.intent || "—"})`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar sugestão");
    } finally {
      setSuggesting(false);
    }
  };

  const handleSend = async () => {
    if (!draft.trim() || !activePhone) return;
    const messageText = draft.trim();
    setSending(true);
    setDraft("");
    try {
      const res = await axios.post(`${API}/messages/whatsapp/send`, {
        phone: activePhone,
        message: messageText,
      });
      // Optimistically append
      setActiveMessages((prev) => [...prev, res.data]);
      // Refresh conversations list
      loadConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar mensagem");
      setDraft(messageText); // restore
    } finally {
      setSending(false);
    }
  };

  const handleStartChat = () => {
    const digits = newPhone.replace(/\D/g, "");
    if (!digits || digits.length < 10) {
      toast.error("Informe um telefone válido (DDD + número)");
      return;
    }
    const normalized = digits.startsWith("55") ? digits : `55${digits}`;
    setActivePhone(normalized);
    setShowNewChat(false);
    setNewPhone("");
    if (!conversations.find((c) => c.phone === normalized)) {
      setConversations((prev) => [
        {
          phone: normalized,
          candidate_id: null,
          candidate_name: "",
          last_message: "",
          last_direction: "outbound",
          last_at: new Date().toISOString(),
          unread: 0,
        },
        ...prev,
      ]);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopiedHook(true);
      toast.success("URL copiada!");
      setTimeout(() => setCopiedHook(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div data-testid="whatsapp-chat-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
            Comunicações
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-emerald-600" />
            WhatsApp
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Converse com candidatos em tempo real via WhatsApp.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="webhook-info-button"
            variant="outline"
            size="sm"
            onClick={() => setShowInfo((s) => !s)}
          >
            <Info className="w-4 h-4 mr-1.5" />
            Como receber mensagens
          </Button>
          <Button
            data-testid="new-chat-button"
            onClick={() => setShowNewChat((s) => !s)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            size="sm"
          >
            <Send className="w-4 h-4 mr-1.5" />
            Nova conversa
          </Button>
        </div>
      </div>

      {/* Webhook info panel */}
      {showInfo && (
        <div
          data-testid="webhook-info-panel"
          className="bg-blue-50 border border-blue-200 rounded-lg p-5"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-blue-900 mb-1">
                Para RECEBER mensagens, configure o webhook no painel Synkly
              </div>
              <p className="text-xs text-blue-800 leading-relaxed mb-3">
                Acesse <strong>Configurações → Synkly</strong>, conecte sua conta WhatsApp e
                configure a URL de webhook abaixo no painel da Synkly para receber mensagens
                dos candidatos em tempo real.
              </p>
              <div className="flex items-center gap-2 bg-white border border-blue-200 rounded-md px-3 py-2">
                <code className="flex-1 text-xs text-slate-700 font-mono truncate">
                  {webhookUrl}
                </code>
                <Button
                  data-testid="copy-webhook-button"
                  size="sm"
                  variant="outline"
                  onClick={copyWebhook}
                  className="shrink-0"
                >
                  {copiedHook ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New chat panel */}
      {showNewChat && (
        <div
          data-testid="new-chat-panel"
          className="bg-white border border-slate-200 rounded-lg p-5"
        >
          <div className="text-sm font-semibold text-slate-900 mb-2">
            Iniciar nova conversa
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="new-chat-phone-input"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="DDD + número (ex: 11987654321)"
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleStartChat()}
            />
            <Button
              data-testid="start-chat-button"
              onClick={handleStartChat}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Iniciar
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            O código do país (+55) será adicionado automaticamente se você não incluir.
          </p>
        </div>
      )}

      {/* Chat layout */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr] h-[calc(100vh-280px)] min-h-[500px]">
        {/* Left: conversations list */}
        <aside className="border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="conversations-search"
                placeholder="Buscar conversa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvos ? (
              <div className="p-6 text-sm text-slate-500 text-center">
                Carregando...
              </div>
            ) : filteredConvos.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  {conversations.length === 0
                    ? "Nenhuma conversa ainda. Inicie uma nova!"
                    : "Nenhum resultado"}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filteredConvos.map((c) => {
                  const isActive = c.phone === activePhone;
                  const displayName = getDisplayName(c);
                  const phoneStr = formatPhoneDisplay(c.phone);
                  return (
                    <li key={c.phone}>
                      <button
                        data-testid={`conversation-${c.phone}`}
                        onClick={() => setActivePhone(c.phone)}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          isActive
                            ? "bg-emerald-50 border-l-2 border-emerald-600"
                            : "hover:bg-slate-50 border-l-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <ConvAvatar conv={c} size="md" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {displayName}
                              </div>
                              <div className="text-[11px] text-slate-500 shrink-0">
                                {formatDay(c.last_at)}
                              </div>
                            </div>
                            {/* Show phone as subtitle when we have a name */}
                            {(c.candidate_name || c.contact_name) && phoneStr && (
                              <div className="text-[11px] text-slate-400 truncate mb-0.5">
                                {phoneStr}
                              </div>
                            )}
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs text-slate-500 truncate">
                                {c.last_direction === "outbound" && (
                                  <span className="text-slate-400">Você: </span>
                                )}
                                {c.last_message
                                  ? c.last_message
                                  : <span className="italic">Sem mensagens</span>}
                              </div>
                              {c.unread > 0 && (
                                <span className="bg-emerald-600 text-white text-[10px] font-semibold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5 shrink-0">
                                  {c.unread}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Right: messages */}
        <section className="flex flex-col min-h-0">
          {!activePhone ? (
            <div className="flex-1 flex items-center justify-center bg-slate-50/50 p-8">
              <div className="text-center max-w-sm">
                <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="heading text-lg font-semibold text-slate-700 mb-1">
                  Selecione uma conversa
                </h3>
                <p className="text-sm text-slate-500">
                  Escolha um candidato à esquerda ou clique em "Nova conversa" para enviar
                  uma mensagem para um novo número.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Active conversation header */}
              <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  {activeConversation && <ConvAvatar conv={activeConversation} size="sm" />}
                  {!activeConversation && (
                    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  {/* Clicking the name area toggles the phone info card */}
                  <div
                    className="min-w-0 cursor-pointer select-none relative"
                    onClick={() => setShowContactInfo((v) => !v)}
                    title="Clique para ver detalhes do contato"
                  >
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {activeConversation ? getDisplayName(activeConversation) : formatPhoneDisplay(activePhone)}
                    </div>
                    <div className="text-xs text-slate-400 inline-flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {formatPhoneDisplay(activePhone) || "via WhatsApp"}
                    </div>
                    {/* Contact info popover */}
                    {showContactInfo && (
                      <div
                        className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg p-4 min-w-[240px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Detalhes do contato</span>
                          <button onClick={() => setShowContactInfo(false)} className="text-slate-400 hover:text-slate-600">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {(activeConversation?.contact_name || activeConversation?.candidate_name) && (
                          <div className="flex items-start gap-2 mb-2">
                            <User className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                            <div>
                              <div className="text-[10px] text-slate-400">Nome</div>
                              <div className="text-sm text-slate-800 font-medium">{getDisplayName(activeConversation)}</div>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <Phone className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[10px] text-slate-400">Telefone</div>
                            <div className="text-sm text-slate-800 font-medium font-mono">
                              {formatPhoneDisplay(activePhone) || (
                                <span className="text-slate-400 font-sans font-normal italic text-xs">
                                  Não disponível (contato com ID privado do WhatsApp)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Maya AI toggle — always visible; disabled when no candidate is linked */}
                  {activeConversation?.candidate_id ? (
                    <button
                      type="button"
                      onClick={handleToggleAutoReply}
                      disabled={togglingAuto}
                      data-testid="auto-reply-toggle"
                      title={
                        autoReplyEnabled
                          ? "Maya está respondendo automaticamente — clique para desativar"
                          : "Ativar Maya para responder automaticamente"
                      }
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                        autoReplyEnabled
                          ? "bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
                          : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      {togglingAuto ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Bot className="w-3.5 h-3.5" />
                      )}
                      {autoReplyEnabled ? "Maya ativa" : "Ativar Maya"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled
                        title="Vincule um candidato para ativar a Maya"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border border-slate-200 text-slate-300 cursor-not-allowed bg-white"
                      >
                        <Bot className="w-3.5 h-3.5" />
                        Ativar Maya
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenLinkModal}
                        title="Vincular este chat a um candidato da plataforma"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border border-violet-200 text-violet-600 hover:bg-violet-50 bg-white transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Vincular candidato
                      </button>
                    </>
                  )}
                  <Button
                    data-testid="refresh-messages-button"
                    variant="ghost"
                    size="sm"
                    onClick={() => loadMessages(activePhone)}
                    className="text-slate-500"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingMessages ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </header>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto bg-slate-50/40 p-5 space-y-2">
                {loadingMessages && activeMessages.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-8">
                    Carregando mensagens...
                  </div>
                ) : activeMessages.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-12">
                    <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    Nenhuma mensagem ainda. Comece a conversa enviando algo abaixo.
                  </div>
                ) : (
                  activeMessages.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`message-${m.id}`}
                      className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                    >
                      <MessageBubble m={m} />
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <footer className="border-t border-slate-200 p-3 bg-white relative">
                {/* Emoji picker popover */}
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className="absolute bottom-full left-0 mb-1 z-50"
                  >
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        setDraft((prev) => prev + emojiData.emoji);
                        setShowEmojiPicker(false);
                      }}
                      lazyLoadEmojis
                      searchPlaceholder="Buscar emoji..."
                      height={380}
                      width={320}
                    />
                  </div>
                )}
                {suggestionMeta && (
                  <div
                    data-testid="suggestion-meta"
                    className="mb-2 flex items-center gap-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2 py-1.5"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span className="font-semibold capitalize">
                      Intenção detectada: {suggestionMeta.intent?.replace(/_/g, " ")}
                    </span>
                    {typeof suggestionMeta.confidence === "number" && (
                      <span className="text-violet-500">
                        · confiança {Math.round((suggestionMeta.confidence || 0) * 100)}%
                      </span>
                    )}
                    {suggestionMeta.reasoning && (
                      <span className="text-violet-500 truncate ml-1 italic">
                        — {suggestionMeta.reasoning}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker((v) => !v)}
                    className={`self-end mb-0.5 p-2 rounded-lg transition-colors ${
                      showEmojiPicker
                        ? "bg-emerald-100 text-emerald-700"
                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    }`}
                    title="Inserir emoji"
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  <textarea
                    data-testid="message-composer"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Digite uma mensagem... (Enter para enviar, Shift+Enter quebra linha)"
                    rows={2}
                    className="flex-1 resize-none border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    disabled={sending}
                  />
                  <div className="flex flex-col gap-1.5">
                    {activeConversation?.candidate_id && (
                      <Button
                        data-testid="ai-suggest-button"
                        type="button"
                        variant="outline"
                        onClick={handleSuggest}
                        disabled={suggesting || sending}
                        className="h-[20px] text-[11px] px-2 border-violet-200 text-violet-700 hover:bg-violet-50"
                        title="Sugerir resposta com IA (preenche o campo)"
                      >
                        {suggesting ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 mr-1" />
                            Sugerir
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      data-testid="send-message-button"
                      onClick={handleSend}
                      disabled={sending || !draft.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-[44px] px-4"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </footer>
            </>
          )}
        </section>
      </div>

      {/* ── Link-candidate modal ────────────────────────────────────────── */}
      <Dialog open={linkModalOpen} onOpenChange={setLinkModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-violet-600" />
              Vincular candidato a esta conversa
            </DialogTitle>
            <DialogDescription>
              Busque pelo nome, e-mail ou telefone do candidato já cadastrado na plataforma.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                autoFocus
                placeholder="Buscar candidato..."
                value={linkSearch}
                onChange={(e) => handleLinkSearchChange(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>

          <div className="mt-1 max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-100">
            {linkSearching ? (
              <div className="flex items-center justify-center py-8 text-slate-400 text-sm gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Buscando...
              </div>
            ) : linkResults.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">
                {linkSearch ? "Nenhum candidato encontrado." : "Nenhum candidato cadastrado."}
              </div>
            ) : (
              linkResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={linking}
                  onClick={() => handleLinkCandidate(c)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-violet-50 transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                    {(c.job_title || c.phone || c.email) && (
                      <div className="text-xs text-slate-400 truncate">
                        {[c.job_title, c.phone || c.email].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  {linking ? (
                    <Loader2 className="w-4 h-4 animate-spin text-violet-500 shrink-0" />
                  ) : (
                    <span className="text-xs text-violet-500 font-medium shrink-0">Vincular</span>
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsappChatPage;
